import { BrowserRouter, Routes, Route, Navigate, useNavigate } from 'react-router-dom';
import { useEffect, Suspense, lazy } from 'react';
import { io } from 'socket.io-client';
import toast, { Toaster } from 'react-hot-toast';
import { AuthProvider, useAuth } from './context/AuthContext';
import { NotificationProvider } from './context/NotificationContext';
import { CallProvider } from './context/CallContext';
import WhatsAppCallModal from './components/WhatsAppCallModal';
import ErrorBoundary from './components/ErrorBoundary';

// Direct import for instantaneous login/register rendering
import AuthPage from './pages/AuthPage';

// Lazy load layouts and non-auth pages for extreme fast initial bundle load
const Layout = lazy(() => import('./components/Layout'));
const SuperAdminLayout = lazy(() => import('./superadmin/SuperAdminLayout'));
const SendMessage = lazy(() => import('./pages/SendMessage'));
const NewCampaign = lazy(() => import('./pages/NewCampaign'));
const Contacts = lazy(() => import('./pages/Contacts'));
const Templates = lazy(() => import('./pages/Templates'));
const Dashboard = lazy(() => import('./pages/Dashboard'));
const SystemTutorial = lazy(() => import('./pages/SystemTutorial'));
const Logs = lazy(() => import('./pages/Logs'));
const Settings = lazy(() => import('./pages/Settings'));
const TeamManagement = lazy(() => import('./pages/TeamManagement'));
const WhatsAppConnect = lazy(() => import('./pages/WhatsAppConnect'));
const ShopifyConnect = lazy(() => import('./pages/ShopifyConnect'));
const ZohoConnect = lazy(() => import('./pages/ZohoConnect'));
const OdooConnect = lazy(() => import('./pages/OdooConnect'));
const InstagramConnect = lazy(() => import('./pages/InstagramConnect'));
const InstagramAutomation = lazy(() => import('./pages/InstagramAutomation'));
const Inbox = lazy(() => import('./pages/Inbox'));
const VoiceCallCenter = lazy(() => import('./pages/VoiceCallCenter'));
const BulkUpload = lazy(() => import('./pages/BulkUpload'));
const FlowList = lazy(() => import('./pages/FlowList'));
const FlowBuilder = lazy(() => import('./pages/FlowBuilder'));
const SAOverview = lazy(() => import('./superadmin/SAOverview'));
const SARegisteredUsers = lazy(() => import('./superadmin/SARegisteredUsers'));
const SAUsers = lazy(() => import('./superadmin/SAUsers'));
const SASettings = lazy(() => import('./superadmin/SASettings'));
const SAPayments = lazy(() => import('./superadmin/SAPayments'));

const API_SOCKET_URL = (process.env.REACT_APP_API_URL || 'https://api.xsquarehub.com/api').replace(/\/api\/?$/, '');

const PlanModuleGate = lazy(() => import('./components/PlanModuleGate'));

const PrivateRoute = ({ children, allowedForAgent = false, module: modKey }) => {
  const { user } = useAuth();
  if (!user) return <Navigate to="/login" />;
  // If superadmin but NOT impersonating an admin — redirect to SA panel
  if (user.role === 'superadmin' && !localStorage.getItem('sa_token'))
    return <Navigate to="/superadmin/overview" />;

  // Tight Security Guard: Staff agents are strictly restricted to /inbox ONLY!
  // Any attempt to visit /home, /campaigns, /contacts, /settings, etc. redirects straight to /inbox!
  if (user.role === 'agent' && !allowedForAgent) {
    return <Navigate to="/inbox" replace />;
  }

  const content = modKey ? (
    <PlanModuleGate module={modKey}>
      {children}
    </PlanModuleGate>
  ) : children;

  return (
    <Layout>
      <ErrorBoundary>
        {content}
      </ErrorBoundary>
    </Layout>
  );
};

const SuperAdminRoute = ({ children }) => {
  const { user } = useAuth();
  if (!user) return <Navigate to="/superadmin/login" />;
  // If impersonating an admin — don't allow SA panel access
  if (localStorage.getItem('sa_token')) return <Navigate to="/home" />;
  if (user.role !== 'superadmin') return <Navigate to="/login" />;
  return (
    <SuperAdminLayout>
      <ErrorBoundary>
        {children}
      </ErrorBoundary>
    </SuperAdminLayout>
  );
};

// Registers React Router's navigate into window so api.js interceptor can use it
function NavigateBridge() {
  const navigate = useNavigate();
  useEffect(() => {
    window.__reactRouterNavigate = navigate;
    return () => {
      window.__reactRouterNavigate = null;
    };
  }, [navigate]);
  return null;
}

// Global WhatsApp token expiry watcher
function TokenExpiryWatcher() {
  const { token } = useAuth();
  const navigate = useNavigate();

  useEffect(() => {
    if (!token) return;
    const socket = io(API_SOCKET_URL, {
      auth: { token },
      transports: ['websocket', 'polling'],
    });

    const handleTokenExpiring = ({ daysLeft }) => {
      toast(
        (t) => (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
            <strong>⚠️ WhatsApp Token Expiring Soon</strong>
            <span style={{ fontSize: '0.82rem' }}>
              Your WhatsApp connection expires in <b>{daysLeft} days</b>. Reconnect to avoid interruption.
            </span>
            <div style={{ display: 'flex', gap: 8, marginTop: 4 }}>
              <button
                onClick={() => {
                  toast.dismiss(t.id);
                  navigate('/whatsapp');
                }}
                style={{
                  background: '#16a34a',
                  color: '#fff',
                  border: 'none',
                  borderRadius: 6,
                  padding: '4px 12px',
                  cursor: 'pointer',
                  fontSize: '0.8rem',
                }}
              >
                Reconnect Now
              </button>
              <button
                onClick={() => toast.dismiss(t.id)}
                style={{
                  background: '#f1f5f9',
                  color: '#64748b',
                  border: 'none',
                  borderRadius: 6,
                  padding: '4px 12px',
                  cursor: 'pointer',
                  fontSize: '0.8rem',
                }}
              >
                Later
              </button>
            </div>
          </div>
        ),
        { duration: 15000 }
      );
    };

    socket.on('whatsapp:token_expiring', handleTokenExpiring);

    return () => {
      socket.off('whatsapp:token_expiring', handleTokenExpiring);
      socket.disconnect();
    };
  }, [token, navigate]);

  return null;
}

function PageLoader() {
  return (
    <div style={{
      display: 'flex',
      flexDirection: 'column',
      alignItems: 'center',
      justifyContent: 'center',
      minHeight: '60vh',
      width: '100%',
      gap: '16px',
    }}>
      <div style={{
        width: '40px',
        height: '40px',
        border: '3px solid rgba(212, 175, 55, 0.2)',
        borderTopColor: '#D4AF37',
        borderRadius: '50%',
        animation: 'appPageSpin 0.75s linear infinite',
      }} />
      <style>{`
        @keyframes appPageSpin {
          to { transform: rotate(360deg); }
        }
      `}</style>
    </div>
  );
}

function RootRedirect() {
  const { user, planStatus } = useAuth();
  if (user?.role === 'agent') return <Navigate to="/inbox" replace />;
  const isModuleAllowed = planStatus?.isModuleAllowed;
  if (isModuleAllowed && isModuleAllowed('instagram') && !isModuleAllowed('campaigns') && !isModuleAllowed('inbox')) {
    return <Navigate to="/instagram" replace />;
  }
  return <Navigate to="/tutorial" replace />;
}

export default function App() {
  return (
    <AuthProvider>
      <BrowserRouter>
        <Toaster
          position="top-right"
          toastOptions={{
            duration: 3000,
            style: { fontSize: '0.875rem', fontWeight: 500, borderRadius: 10, padding: '12px 16px' },
            success: { style: { background: '#f0fdf4', color: '#16a34a', border: '1px solid #bbf7d0' } },
            error:   { style: { background: '#fef2f2', color: '#dc2626', border: '1px solid #fecaca' } },
          }}
        />
        <NavigateBridge />
        <TokenExpiryWatcher />
        <NotificationProvider>
          <CallProvider>
            <WhatsAppCallModal />
            <ErrorBoundary message="App crashed. Please refresh the page.">
              <Suspense fallback={<PageLoader />}>
                <Routes>
                {/* Admin routes */}
                <Route path="/login"    element={<AuthPage />} />
                <Route path="/register" element={<AuthPage />} />
                <Route path="/tutorial"            element={<PrivateRoute><SystemTutorial /></PrivateRoute>} />
                <Route path="/home"                element={<PrivateRoute><Dashboard /></PrivateRoute>} />
                <Route path="/campaigns"           element={<PrivateRoute module="campaigns"><SendMessage /></PrivateRoute>} />
                <Route path="/dashboard"            element={<PrivateRoute module="campaigns"><NewCampaign /></PrivateRoute>} />
                <Route path="/campaigns/new"        element={<PrivateRoute module="campaigns"><NewCampaign /></PrivateRoute>} />
                <Route path="/contacts"             element={<PrivateRoute module="contacts"><Contacts /></PrivateRoute>} />
                <Route path="/contacts/bulk-upload" element={<PrivateRoute module="contacts"><BulkUpload /></PrivateRoute>} />
                <Route path="/templates"            element={<PrivateRoute module="templates"><Templates /></PrivateRoute>} />
                <Route path="/logs"                 element={<PrivateRoute module="logs"><Logs /></PrivateRoute>} />
                <Route path="/team"                 element={<PrivateRoute module="team"><TeamManagement /></PrivateRoute>} />
                <Route path="/settings"             element={<PrivateRoute><Settings /></PrivateRoute>} />
                <Route path="/whatsapp"             element={<PrivateRoute module="campaigns"><WhatsAppConnect /></PrivateRoute>} />
                <Route path="/shopify"              element={<PrivateRoute module="crm"><ShopifyConnect /></PrivateRoute>} />
                <Route path="/zoho"                 element={<PrivateRoute module="crm"><ZohoConnect /></PrivateRoute>} />
                <Route path="/odoo"                 element={<PrivateRoute module="crm"><OdooConnect /></PrivateRoute>} />
                <Route path="/instagram"            element={<PrivateRoute module="instagram"><InstagramConnect /></PrivateRoute>} />
                <Route path="/instagram/callback"  element={<PrivateRoute module="instagram"><InstagramConnect /></PrivateRoute>} />
                <Route path="/instagram/automation"element={<PrivateRoute module="instagram"><InstagramAutomation /></PrivateRoute>} />
                <Route path="/inbox"                element={<PrivateRoute allowedForAgent={true} module="inbox"><Inbox /></PrivateRoute>} />
                <Route path="/calls"                element={<PrivateRoute module="calls"><VoiceCallCenter /></PrivateRoute>} />
                <Route path="/flows"                element={<PrivateRoute module="flows"><FlowList /></PrivateRoute>} />
                <Route path="/flows/new"            element={<PrivateRoute module="flows"><FlowBuilder /></PrivateRoute>} />
                <Route path="/flows/:id"            element={<PrivateRoute module="flows"><FlowBuilder /></PrivateRoute>} />

                {/* Super Admin routes */}
                <Route path="/superadmin/overview" element={<SuperAdminRoute><SAOverview /></SuperAdminRoute>} />
                <Route path="/superadmin/registered-users" element={<SuperAdminRoute><SARegisteredUsers /></SuperAdminRoute>} />
                <Route path="/superadmin/users"    element={<SuperAdminRoute><SAUsers /></SuperAdminRoute>} />
                <Route path="/superadmin/payments" element={<SuperAdminRoute><SAPayments /></SuperAdminRoute>} />
                <Route path="/superadmin/settings" element={<SuperAdminRoute><SASettings /></SuperAdminRoute>} />
                <Route path="/superadmin"          element={<Navigate to="/superadmin/overview" />} />

                <Route path="/" element={<RootRedirect />} />
                <Route path="*" element={<Navigate to="/login" />} />
              </Routes>
              </Suspense>
            </ErrorBoundary>
          </CallProvider>
        </NotificationProvider>
      </BrowserRouter>
    </AuthProvider>
  );
}
